// ============================================================================
// SHARED AI ASSISTANT CHAT - Unified Chat Interface for All AI Assistants
// Reusable across: Total Operations, Total Attendance, Total CRM, Total PBX,
//                  Total Support, Total Finance, Total Workplace, Total Integration
// Redesigned: January 2026 - Relatus.AI inspired styling
// Updated: Uses unified theming from clientBranding primary color
// ============================================================================

'use client';

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';

import type { ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';

import {
  Sparkles,
  Send,
  RefreshCw,
  ThumbsUp,
  ThumbsDown,
  Loader2,
  X,
  Minimize2,
  Copy,
  Check,
  Paperclip,
  Mic,
  MicOff,
  StopCircle,
  History,
  MessageCircle,
  FileText,
  Image,
  File,
  AlertTriangle,
  HelpCircle,
  Brain,
  ArrowLeft,
  ArrowRight,
  GraduationCap
} from 'lucide-react';
import { cn } from '@/lib/utils';
import ModuleHeader from '@/components/modules/ModuleHeader';
import type { HeaderAction } from '@/components/modules/ModuleHeader';
import { useTheme } from '@/contexts/ThemeContext';
import {
  createSpeechToTextController,
  isSpeechToTextSupported,
  type SpeechToTextController,
} from '@/lib/speechToText';
import { authService } from '@/lib/authService';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';
import { notify } from '@/lib/notifications';
import { isCapacitor } from '@/lib/platformUtils';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export interface AIMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: string;
  attachments?: AIAttachment[];
  actions?: AISuggestedAction[];
  feedback?: 'positive' | 'negative' | null;
}

export interface AIAttachment {
  type: string;
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- shared chat attachments carry strongly-typed payloads per assistant module
  data: any;
  /** Set when the structured payload was salvaged from a truncated response —
   *  it may be missing trailing content. Renderers can warn on apply. */
  truncated?: boolean;
}

export interface AISuggestedAction {
  id: string;
  label: string;
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- suggested action payloads vary by module and are narrowed at use sites
  data?: any;
  applied?: boolean;
}

export interface QuickAction {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  prompt?: string; // Optional prompt text to populate input when clicked
  color?: string;  // Background color for the quick action button
}

export interface ExamplePrompt {
  icon: React.ComponentType<{ className?: string }>;
  text: string;
  category?: string;
  color?: string; // Background color for the prompt card
}

export interface SessionHistoryItem {
  id: string;
  date: string;
  moduleContext: string;
  messageCount: number;
  lastActivity: string;
}

export interface AIAssistantChatProps {
  // Identity & branding
  title: string;
  subtitle?: string;
  welcomeTitle?: string;
  welcomeMessage?: string;
  inputPlaceholder?: string;
  
  // Module context for header display (e.g., "CRM", "Operations", "Support")
  moduleContext?: string;
  moduleKey?: string;
  
  // User info for personalized welcome
  userName?: string; // User's first name for "Welcome, [Name]"
  
  // State
  messages: AIMessage[];
  isGenerating: boolean;
  creditsRemaining?: number;
  creditsTotal?: number;
  creditsResetDate?: string;
  showCredits?: boolean;
  
  // Quick actions & prompts
  quickActions?: QuickAction[];
  examplePrompts?: ExamplePrompt[];
  showQuickActions?: boolean;
  showQuickActionsInHeader?: boolean; // Show quick actions as tabs in the header (Total Connect style)
  
  // Event handlers
  onSendMessage: (message: string, files?: globalThis.File[], options?: { provider?: string; model?: string }) => void | Promise<void>;
  onQuickAction?: (actionId: string) => void;
  onNewChat?: () => void;
  onFeedback?: (messageId: string, feedback: 'positive' | 'negative') => void;
  onApplyAction?: (action: AISuggestedAction) => void;
  onMinimize?: () => void;
  onClose?: () => void;
  
  // Provider toggle (AI credit system — Phase 10)
  showProviderToggle?: boolean;
  /** Force the provider/model sent with each message — overrides the
   *  total-vision default (e.g. CV editing needs Gemini's context window). */
  providerOverride?: { provider?: string; model?: string };
  
  // History
  showHistory?: boolean;  // Show history button in header
  onLoadSession?: (sessionId: string, sessionDate: string) => void | Promise<void>;
  // Custom history fetcher — overrides the default /api/v1/ai-assistant/session
  // endpoint. Used by the unified AI surface to list coaching sessions.
  onFetchHistory?: () => Promise<SessionHistoryItem[]>;
  externalHistoryOpen?: boolean;
  onHistoryOpenChange?: (open: boolean) => void;
  
  // Custom renderers
  renderAttachment?: (attachment: AIAttachment) => ReactNode;
  renderActions?: (actions: AISuggestedAction[], onApply: (action: AISuggestedAction) => void) => ReactNode;
  
  // Layout & styling
  className?: string;
  headerClassName?: string;
  primaryColor?: string;  // Fallback if useUnifiedTheming is false
  gradientFrom?: string;  // Fallback if useUnifiedTheming is false
  gradientTo?: string;    // Fallback if useUnifiedTheming is false
  minimizable?: boolean;
  closable?: boolean;
  isMinimized?: boolean;
  showDisclaimer?: boolean;
  disclaimerText?: string;
  
  // Unified theming - when true, uses clientBranding primary color from useTheme
  useUnifiedTheming?: boolean;
  
  // Header icon - can be a component or use BrandedLoadingSpinner
  headerIcon?: ReactNode;
  useBrandedSpinner?: boolean;
  spinnerSize?: 'sm' | 'md' | 'lg';

  // Disable the built-in initial scroll reset (useful for embedded chat panels)
  disableInitialScrollReset?: boolean;
  
  // Pre-populate the input field (e.g., when navigating from another view with context)
  initialPrompt?: string;

  // Composer sizing overrides (useful for embedded assistants)
  inputRows?: number;
  inputMinHeightClassName?: string;
  inputMaxHeightClassName?: string;
  maxInputChars?: number;
  showInputCharCounter?: boolean;
  inputCounterLabel?: string;
  allowAttachments?: boolean;
  headerStorageKey?: string;
  
  // When true, hides the built-in ModuleHeader (use when an external ModuleHeader is rendered above)
  hideHeader?: boolean;

  // Header mode toggle — when true (default), shows a "Coach" control that
  // dispatches 'ai-coach-open' and closes this panel, switching the unified
  // AI surface to the AI Training Coach. Set false for embedded contexts
  // where the coach toggle doesn't make sense.
  coachToggle?: boolean;

  // Extra header actions injected before the built-in ones — used by the
  // unified AI surface to add a "Tours" button and a TTS mute toggle.
  extraHeaderActions?: HeaderAction[];

  // Optional node rendered beside the header title — intended for contextual
  // help links such as <UserDocsLink/> (same contract as ModuleHeader's
  // titleExtra). Applies to both the floating panel header and ModuleHeader.
  titleExtra?: React.ReactNode;

  // Floating panel mode — renders as a fixed bottom-right chat panel (like the
  // GuidedTour chat) instead of an embedded full-height container. Uses a
  // blue border to distinguish it from the coach's red border.
  floatingPanel?: boolean;
  // Optional override for the floating panel's border color (defaults to blue-500).
  floatingBorderColor?: string;
  // When in floating mode, show a launcher button when closed (internal visibility).
  // If false, the parent controls mount/unmount. Defaults to false (parent-controlled).
  floatingShowLauncher?: boolean;
  // Overrides the floating header badge text (default: "AI Assistant") — e.g.
  // "Training Coach" when the chat is scoped to a tour flow.
  floatingBadgeLabel?: string;
  // Replaces the built-in floating minimize (internal pill) — the unified AI
  // surface routes this to the tour engine's minimize instead.
  onFloatingMinimize?: () => void;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * Shift a hex color's hue by a specified number of degrees for gradient effect
 */
function shiftColorHue(hex: string, degrees: number): string {
  let r = 0, g = 0, b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  } else {
    return hex;
  }
  
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  
  h = (h + degrees / 360) % 1;
  if (h < 0) h += 1;
  
  let r1: number, g1: number, b1: number;
  if (s === 0) {
    r1 = g1 = b1 = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r1 = hue2rgb(p, q, h + 1/3);
    g1 = hue2rgb(p, q, h);
    b1 = hue2rgb(p, q, h - 1/3);
  }
  
  const toHex = (x: number) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(r1)}${toHex(g1)}${toHex(b1)}`;
}

function getErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }

  return fallback;
}

const WORK_ITEM_LINK_REGEX = /\bwi:(\d+)\b/g;

function preprocessWorkItemLinks(content: string): string {
  return content.replace(WORK_ITEM_LINK_REGEX, (_match, id) => {
    return `[Work Item #${id}](wi:${id})`;
  });
}

function renderMessageContent(content: string): ReactNode {
  const processed = preprocessWorkItemLinks(content);

  return (
    <ReactMarkdown
      components={{
        a: ({ href, children, ...props }) => {
          if (href?.startsWith('wi:')) {
            const workItemId = href.slice(3);
            return (
              <a
                href={`#wi-${workItemId}`}
                onClick={(e) => {
                  e.preventDefault();
                  if (typeof window !== 'undefined') {
                    window.dispatchEvent(new CustomEvent('openWorkItem', { detail: { id: workItemId } }));
                  }
                }}
                className="underline underline-offset-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors cursor-pointer"
                {...props}
              >
                {children}
              </a>
            );
          }
          const isExternal = href?.startsWith('http://') || href?.startsWith('https://');
          const isWorkItemLink = href?.startsWith('/wi/');
          return (
            <a
              href={href}
              target={isExternal ? '_blank' : undefined}
              rel={isExternal ? 'noopener noreferrer' : undefined}
              className="underline underline-offset-2 text-blue-600 dark:text-blue-400 break-all hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
              {...(isWorkItemLink ? { 'data-work-item-link': 'true' } : {})}
              {...props}
            >
              {children}
            </a>
          );
        },
        ul: ({ children }) => (
          <ul className="list-disc pl-5 my-1.5 space-y-0.5">{children}</ul>
        ),
        ol: ({ children }) => (
          <ol className="list-decimal pl-5 my-1.5 space-y-0.5">{children}</ol>
        ),
        li: ({ children }) => <li className="leading-relaxed">{children}</li>,
        strong: ({ children }) => (
          <strong className="font-semibold text-gray-900 dark:text-white">{children}</strong>
        ),
        h1: ({ children }) => (
          <h3 className="text-base font-semibold mt-2 mb-1 text-gray-900 dark:text-white">{children}</h3>
        ),
        h2: ({ children }) => (
          <h4 className="text-sm font-semibold mt-2 mb-1 text-gray-900 dark:text-white">{children}</h4>
        ),
        h3: ({ children }) => (
          <h5 className="text-sm font-medium mt-1.5 mb-0.5 text-gray-900 dark:text-white">{children}</h5>
        ),
        p: ({ children }) => <p className="leading-relaxed mb-1 last:mb-0">{children}</p>,
        code: ({ children }) => (
          <code className="px-1 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-xs font-mono">{children}</code>
        ),
        blockquote: ({ children }) => (
          <blockquote className="border-l-2 border-gray-300 dark:border-gray-600 pl-3 my-1.5 italic text-gray-600 dark:text-gray-400">{children}</blockquote>
        ),
        hr: () => <hr className="my-2 border-gray-200 dark:border-gray-700" />,
        table: ({ children }) => (
          <div className="overflow-x-auto my-2">
            <table className="min-w-full text-xs border border-gray-200 dark:border-gray-700">{children}</table>
          </div>
        ),
        th: ({ children }) => (
          <th className="px-2 py-1 border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 font-semibold text-left">{children}</th>
        ),
        td: ({ children }) => (
          <td className="px-2 py-1 border border-gray-200 dark:border-gray-700">{children}</td>
        ),
      }}
    >
      {processed}
    </ReactMarkdown>
  );
}

function mergeSpeechIntoChatInput(baseText: string, transcript: string): string {
  const spokenText = transcript.trim();
  if (!spokenText) {
    return baseText;
  }

  const trimmedBase = baseText.trimEnd();
  if (!trimmedBase) {
    return spokenText;
  }

  return `${trimmedBase}\n${spokenText}`;
}

function appendRecordedSpeechToChatInput(baseText: string, transcript: string): string {
  const spokenText = transcript.trim();
  if (!spokenText) {
    return baseText;
  }

  const trimmedBase = baseText.trimEnd();
  if (!trimmedBase) {
    return spokenText;
  }

  return `${trimmedBase}\n\n${spokenText}`;
}

function resolveModuleRecordKey(moduleContext?: string, explicitModuleKey?: string): string {
  const normalizedExplicitModuleKey = explicitModuleKey?.trim();
  if (normalizedExplicitModuleKey) {
    return normalizedExplicitModuleKey;
  }

  const normalizedContext = moduleContext?.trim().toLowerCase();
  if (!normalizedContext) {
    return '';
  }

  if (normalizedContext.startsWith('total-')) {
    return normalizedContext;
  }

  const moduleContextToKeyMap: Record<string, string> = {
    forms: 'total-forms',
    finance: 'total-finance',
    accounts: 'total-finance',
    operations: 'total-operations',
    proposals: 'total-operations',
    'service-operations': 'total-operations',
    'fleet-management': 'total-operations',
    'time-tracking': 'total-operations',
    'support-desk': 'total-operations',
    'appointment-scheduling': 'total-operations',
    'lead-management': 'total-operations',
    'waste-management': 'total-operations',
    'asset-maintenance': 'total-operations',
    'tools-equipment': 'total-operations',
    'workplace-management': 'total-operations',
    crm: 'total-crm',
    pabx: 'total-pabx',
    support: 'total-support',
    workforce: 'total-workforce',
    hr: 'total-workforce',
    attendance: 'total-attendance',
    scheduler: 'total-attendance',
    connect: 'total-connect',
  };

  return moduleContextToKeyMap[normalizedContext] || normalizedContext;
}

export default function AIAssistantChat({
  // Identity
  title,
  subtitle,
  welcomeTitle,
  welcomeMessage,
  inputPlaceholder = 'Ask me anything...',
  
  // Module context
  moduleContext,
  moduleKey,
  
  // User info
  userName,
  
  // State
  messages,
  isGenerating,
  creditsRemaining = 0,
  creditsTotal = 0,
  creditsResetDate,
  showCredits = true,
  
  // Quick actions
  quickActions = [],
  examplePrompts = [],
  showQuickActions = true,
  showQuickActionsInHeader = false,
  
  // Handlers
  onSendMessage,
  onQuickAction,
  onNewChat,
  onFeedback,
  onApplyAction,
  onMinimize,
  onClose,

  // Provider toggle
  showProviderToggle = false,
  providerOverride,
  
  // History
  showHistory = false,
  onLoadSession,
  onFetchHistory,
  externalHistoryOpen,
  onHistoryOpenChange,
  
  // Custom renderers
  renderAttachment,
  renderActions,
  
  // Layout
  className,
  headerClassName,
  primaryColor: propPrimaryColor,
  gradientFrom: propGradientFrom,
  gradientTo: propGradientTo,
  minimizable = false,
  closable = false,
  isMinimized = false,
  showDisclaimer = true,
  disclaimerText = 'AI responses may not always be accurate. Verify important information.',
  
  // Unified theming
  useUnifiedTheming = true,  // Default to unified theming
  
  // Header icon
  headerIcon,
  disableInitialScrollReset = false,
  initialPrompt,
  inputRows = 1,
  inputMinHeightClassName = 'min-h-[40px]',
  inputMaxHeightClassName = 'max-h-[180px]',
  maxInputChars = 1500,
  showInputCharCounter = true,
  inputCounterLabel = 'Prompt',
  allowAttachments = true,
  headerStorageKey,
  hideHeader = false,
  coachToggle = true,
  extraHeaderActions,
  titleExtra,
  floatingPanel = false,
  floatingBorderColor,
  floatingShowLauncher = false,
  floatingBadgeLabel,
  onFloatingMinimize,
}: AIAssistantChatProps) {
  // Get client branding for unified theming
  const { clientBranding } = useTheme();
  
  // Determine actual colors - prefer unified theming from clientBranding
  const brandPrimaryColor = clientBranding?.primary_color || '#0d9488';
  const primaryColor = useUnifiedTheming ? brandPrimaryColor : (propPrimaryColor || '#0d9488');
  const gradientFrom = useUnifiedTheming ? primaryColor : (propGradientFrom || primaryColor);
  const gradientTo = useUnifiedTheming ? shiftColorHue(primaryColor, 15) : (propGradientTo || '#06b6d4');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesTopRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const [inputText, setInputText] = useState(initialPrompt || '');
  const normalizedMaxInputChars = Number.isFinite(maxInputChars) && maxInputChars > 0
    ? Math.floor(maxInputChars)
    : 1500;

  // Prompt/quick-action suggestion UI is intentionally disabled globally.
  void quickActions;
  void examplePrompts;
  void showQuickActions;
  void showQuickActionsInHeader;
  void onQuickAction;
  
  const focusInput = useCallback(() => {
    const input = inputRef.current;
    if (!input) {
      return;
    }

    try {
      input.focus({ preventScroll: true });
    } catch {
      input.focus();
    }
  }, []);

  // If initialPrompt changes externally, populate the input and focus
  const prevInitialPromptRef = useRef(initialPrompt);
  useEffect(() => {
    if (initialPrompt && initialPrompt !== prevInitialPromptRef.current) {
      setInputText(initialPrompt);
      prevInitialPromptRef.current = initialPrompt;
      setTimeout(() => focusInput(), 100);
    }
  }, [focusInput, initialPrompt]);
  const [copiedMessageId, setCopiedMessageId] = useState<string | null>(null);
  const [localHistoryOpen, setLocalHistoryOpen] = useState(false);
  const [sessionHistory, setSessionHistory] = useState<SessionHistoryItem[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingSessionId, setLoadingSessionId] = useState<string | null>(null);
  const historyOpen = externalHistoryOpen ?? localHistoryOpen;
  const [isDragOver, setIsDragOver] = useState(false);

  // ---- Floating panel state (only used when floatingPanel=true) ----
  const [floatingMinimized, setFloatingMinimized] = useState(false);
  const [floatingDockSide, setFloatingDockSide] = useState<'right' | 'left'>('right');
  const [floatingVisible, setFloatingVisible] = useState(true);
  // Portal target — render the floating panel into document.body via a portal
  // so its fixed positioning escapes any parent stacking/containing block
  // (e.g. modals with overflow-hidden or transform-based containment).
  // This mirrors the approach used by GuidedTour chat.
  const [portalTarget, setPortalTarget] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalTarget(document.body);
  }, []);
  // Default to a blue border (coach uses red). A custom hex color is applied
  // via inline style since dynamic Tailwind classes aren't JIT-generated.
  const floatingBorderClass = 'border-blue-500 dark:border-blue-500';

  const setHistoryOpen = useCallback((open: boolean) => {
    if (externalHistoryOpen !== undefined) {
      onHistoryOpenChange?.(open);
      return;
    }

    setLocalHistoryOpen(open);
  }, [externalHistoryOpen, onHistoryOpenChange]);

  // Internal handler for guided-tour suggestion actions. The chat API
  // extracts <tour_suggestion> tags from AI responses and surfaces them as
  // actions with type 'start_tour'. We handle these universally here so
  // every module's assistant can launch tours without bespoke wiring —
  // dispatching the global 'guided-tour-open' window event that the
  // GuidedTourProvider listens for.
  const handleStartTourAction = useCallback((action: AISuggestedAction) => {
    if (typeof window === 'undefined') return;
    const moduleKey = action.data?.moduleKey;
    const flowId = action.data?.flowId;
    if (!moduleKey || !flowId) return;
    window.dispatchEvent(
      new CustomEvent('guided-tour-open', {
        detail: { moduleKey, flowId },
      }),
    );
  }, []);

  // Mode toggle — close this assistant and open the AI Training Coach.
  // Mutual exclusivity: module pages also close their assistant on
  // 'ai-coach-open', so the two panels can never overlap.
  const handleCoachToggle = useCallback(() => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('ai-coach-open'));
    }
    onClose?.();
  }, [onClose]);

  // Unified apply-action handler: intercept start_tour and open_coach actions
  // locally, then forward to the module-provided onApplyAction for any other
  // type (and for start_tour/open_coach if the module also wants to react).
  const handleApplyActionInternal = useCallback((action: AISuggestedAction) => {
    if (action.type === 'start_tour') {
      handleStartTourAction(action);
    } else if (action.type === 'open_coach') {
      handleCoachToggle();
    }
    onApplyAction?.(action);
  }, [handleStartTourAction, handleCoachToggle, onApplyAction]);

  // File attachment state
  const [attachedFiles, setAttachedFiles] = useState<{ name: string; size: number; type: string; file: globalThis.File }[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Voice recording state
  const [isListening, setIsListening] = useState(false);
  const [isSpeechStarting, setIsSpeechStarting] = useState(false);
  const [isAudioRecording, setIsAudioRecording] = useState(false);
  const [isTranscribingAudio, setIsTranscribingAudio] = useState(false);
  const [audioRecordingDuration, setAudioRecordingDuration] = useState(0);
  const [speechRecognitionSupported, setSpeechRecognitionSupported] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const speechRecognitionRef = useRef<SpeechToTextController | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const audioRecordingTimerRef = useRef<number | null>(null);
  const speechDraftBaseRef = useRef('');
  
  useEffect(() => {
    if (!allowAttachments) {
      setAttachedFiles([]);
      setIsDragOver(false);
    }
  }, [allowAttachments]);

  useEffect(() => {
    let cancelled = false;

    void isSpeechToTextSupported().then((supported) => {
      if (!cancelled) {
        setSpeechRecognitionSupported(supported);
      }
    });

    return () => {
      cancelled = true;
    };
  }, []);

  // Provider is always total-vision (self-hosted) — no toggle

  const resolvedHeaderIcon = useMemo<React.ElementType>(() => {
    if (typeof headerIcon === 'function') {
      return headerIcon as React.ElementType;
    }

    if (React.isValidElement(headerIcon) && typeof headerIcon.type !== 'string') {
      return headerIcon.type as React.ElementType;
    }

    return Sparkles;
  }, [headerIcon]);

  const resolvedHeaderStorageKey = useMemo(() => {
    if (headerStorageKey?.trim()) {
      return headerStorageKey;
    }

    const keySource = `${moduleContext || title || 'assistant'}_header`
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');

    return `ta_${keySource || 'ai_assistant_header'}`;
  }, [headerStorageKey, moduleContext, title]);

  const resolvedModuleKey = useMemo(() => {
    return resolveModuleRecordKey(moduleContext, moduleKey);
  }, [moduleContext, moduleKey]);

  const headerCenterContent = useMemo(() => {
    if (!showCredits) {
      return null;
    }

    const creditToneClass = creditsTotal > 0 && creditsRemaining <= 0
      ? 'bg-red-500/20 text-red-100'
      : creditsTotal > 0 && (creditsRemaining / creditsTotal) <= 0.1
        ? 'bg-orange-500/20 text-orange-100'
        : creditsTotal > 0 && (creditsRemaining / creditsTotal) <= 0.25
          ? 'bg-yellow-500/20 text-yellow-100'
          : 'bg-white/10 text-white';

    const creditIconClass = creditsTotal > 0 && creditsRemaining <= 0
      ? 'text-red-200'
      : creditsTotal > 0 && (creditsRemaining / creditsTotal) <= 0.1
        ? 'text-orange-200'
        : 'text-yellow-200';

    const isUnlimited = creditsTotal >= 999999;

    return (
      <div className={cn('inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs font-semibold', creditToneClass)}>
        <Sparkles className={cn('h-3.5 w-3.5', creditIconClass)} />
        {isUnlimited ? (
          <span>Unlimited</span>
        ) : (
          <span>{creditsRemaining} credit{creditsRemaining === 1 ? '' : 's'}</span>
        )}
        {!isUnlimited && creditsResetDate && (
          <span className="hidden xl:inline text-[11px] font-medium text-white/80">
            Resets {new Date(creditsResetDate).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}
          </span>
        )}
      </div>
    );
  }, [creditsRemaining, creditsResetDate, creditsTotal, showCredits]);

  // Scroll to top on mount (when AI Assistant tab is selected)
  useEffect(() => {
    if (disableInitialScrollReset) {
      return;
    }

    // Use a small delay to ensure DOM is ready
    const timer = setTimeout(() => {
      // Try multiple scroll targets to ensure we catch the right scrollable container
      window.scrollTo({ top: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
      
      // Also scroll any parent with overflow
      let parent = containerRef.current?.parentElement;
      while (parent) {
        const style = window.getComputedStyle(parent);
        if (style.overflow === 'auto' || style.overflowY === 'auto' || 
            style.overflow === 'scroll' || style.overflowY === 'scroll') {
          parent.scrollTop = 0;
        }
        parent = parent.parentElement;
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [disableInitialScrollReset]);

  // Scroll to top of component when container is clicked (outside of interactive elements)
  const handleContainerClick = useCallback((e: React.MouseEvent) => {
    // Only scroll to top if clicking on the container background, not on interactive elements
    const target = e.target as HTMLElement;
    if (target === containerRef.current || target.classList.contains('scroll-to-top-trigger')) {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        messagesTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, []);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (messages.length === 0 && !isGenerating) {
      return;
    }

    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }

    container.scrollTo({
      top: container.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, isGenerating]);

  // Fetch session history for the history sidebar
  const fetchHistory = useCallback(async () => {
    if (!showHistory) return;
    setLoadingHistory(true);
    try {
      if (onFetchHistory) {
        setSessionHistory(await onFetchHistory());
        return;
      }
      if (!moduleContext) return;
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;

      const response = await fetch(`/api/v1/ai-assistant/session?moduleContext=${encodeURIComponent(moduleContext)}`, {
        headers: { 'Authorization': `Bearer ${tokens.access_token}` }
      });
      const data = await response.json();
      if (data.success && Array.isArray(data.sessions)) {
        setSessionHistory(data.sessions);
      }
    } catch (error) {
      if (!isSilentFetchError(error)) console.error('Failed to fetch session history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [showHistory, moduleContext, onFetchHistory]);

  // Fetch history when sidebar opens
  useEffect(() => {
    if (historyOpen) {
      fetchHistory();
    }
  }, [historyOpen, fetchHistory]);

  const handleLoadSession = useCallback(async (sessionId: string, sessionDate: string) => {
    if (!onLoadSession) return;
    setLoadingSessionId(sessionId);
    try {
      await onLoadSession(sessionId, sessionDate);
      setHistoryOpen(false);
    } catch (error) {
      if (!isSilentFetchError(error)) console.error('Failed to load session:', error);
    } finally {
      setLoadingSessionId(null);
    }
  }, [onLoadSession, setHistoryOpen]);

  // Format session date for display
  const formatSessionDate = useCallback((dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) return 'Today';
    if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
    
    const diffDays = Math.floor((today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (diffDays < 7) return date.toLocaleDateString('en-US', { weekday: 'long' });
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined });
  }, []);

  const handleSendMessage = useCallback(async (overrideMessage?: string) => {
    const messageToSend = overrideMessage || inputText;
    if (!messageToSend.trim() || isGenerating) return;
    
    const filesToSend = allowAttachments && attachedFiles.length > 0 ? attachedFiles.map(f => f.file) : undefined;
    const providerOption = providerOverride ?? { provider: 'total-vision' as const, model: 'qwen3.5:4b' };
    setInputText('');
    setAttachedFiles([]);
    await onSendMessage(messageToSend, filesToSend, providerOption);
  }, [allowAttachments, attachedFiles, inputText, isGenerating, onSendMessage, providerOverride]);

  const handleInputChange = useCallback((nextValue: string) => {
    if (nextValue.length <= normalizedMaxInputChars) {
      setInputText(nextValue);
      return;
    }

    setInputText(nextValue.slice(0, normalizedMaxInputChars));
  }, [normalizedMaxInputChars]);

  const handleCopyMessage = useCallback(async (messageId: string, content: string) => {
    await navigator.clipboard.writeText(content);
    setCopiedMessageId(messageId);
    setTimeout(() => setCopiedMessageId(null), 2000);
  }, []);

  const handleNewChatClick = useCallback(() => {
    setInputText('');
    setAttachedFiles([]);
    setIsDragOver(false);
    if (onNewChat) {
      onNewChat();
    }
  }, [onNewChat]);

  const headerActions = useMemo<HeaderAction[]>(() => {
    const actions: HeaderAction[] = [...(extraHeaderActions ?? [])];

    if (coachToggle) {
      actions.push({
        id: 'coach',
        icon: GraduationCap,
        label: 'Coach',
        onClick: handleCoachToggle,
        title: 'Switch to AI Training Coach',
        showLabel: 'never',
      });
    }

    if (showHistory && onLoadSession) {
      actions.push({
        id: 'history',
        icon: History,
        label: 'History',
        onClick: () => setHistoryOpen(!historyOpen),
        title: 'Chat History',
        showLabel: 'never',
        variant: historyOpen ? 'solid' : 'ghost',
      });
    }

    if (onNewChat) {
      actions.push({
        id: 'new-chat',
        icon: RefreshCw,
        label: 'New Chat',
        onClick: handleNewChatClick,
        title: 'New Chat',
        showLabel: 'never',
      });
    }

    if (minimizable && onMinimize) {
      actions.push({
        id: 'minimize',
        icon: Minimize2,
        label: 'Minimize',
        onClick: onMinimize,
        title: 'Minimize',
        showLabel: 'never',
      });
    }

    if (closable && onClose) {
      actions.push({
        id: 'close',
        icon: X,
        label: 'Close',
        onClick: onClose,
        title: 'Close',
        showLabel: 'never',
      });
    }

    return actions;
  }, [closable, coachToggle, extraHeaderActions, handleCoachToggle, handleNewChatClick, historyOpen, minimizable, onClose, onLoadSession, onMinimize, onNewChat, setHistoryOpen, showHistory]);

  // ---- FILE ATTACHMENT HANDLERS ----
  const addFilesToAttachments = useCallback((files: globalThis.File[]) => {
    if (!files.length) return;

    const newFiles = files.map((file) => ({
      name: file.name,
      size: file.size,
      type: file.type,
      file,
    }));

    setAttachedFiles((prev) => [...prev, ...newFiles].slice(0, 5));
  }, []);

  const handleAttachClick = useCallback(() => {
    if (!allowAttachments) {
      return;
    }

    fileInputRef.current?.click();
  }, [allowAttachments]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (!allowAttachments) {
      return;
    }

    const files = e.target.files;
    if (!files) return;

    addFilesToAttachments(Array.from(files));
    // Reset input so same file can be selected again
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [addFilesToAttachments, allowAttachments]);

  const handleRemoveFile = useCallback((index: number) => {
    setAttachedFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  const dragEventHasFiles = useCallback((event: React.DragEvent<HTMLElement>) => {
    return Array.from(event.dataTransfer?.types || []).includes('Files');
  }, []);

  const handleDragEnter = useCallback((event: React.DragEvent<HTMLElement>) => {
    if (!allowAttachments) {
      return;
    }

    if (!dragEventHasFiles(event)) return;

    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(true);
  }, [allowAttachments, dragEventHasFiles]);

  const handleDragOver = useCallback((event: React.DragEvent<HTMLElement>) => {
    if (!allowAttachments) {
      return;
    }

    if (!dragEventHasFiles(event)) return;

    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'copy';

    if (!isDragOver) {
      setIsDragOver(true);
    }
  }, [allowAttachments, dragEventHasFiles, isDragOver]);

  const handleDragLeave = useCallback((event: React.DragEvent<HTMLElement>) => {
    if (!allowAttachments) {
      return;
    }

    if (!dragEventHasFiles(event)) return;

    event.preventDefault();
    event.stopPropagation();

    const nextTarget = event.relatedTarget as Node | null;
    if (!nextTarget || !event.currentTarget.contains(nextTarget)) {
      setIsDragOver(false);
    }
  }, [allowAttachments, dragEventHasFiles]);

  const handleDrop = useCallback((event: React.DragEvent<HTMLElement>) => {
    if (!allowAttachments) {
      return;
    }

    if (!dragEventHasFiles(event)) return;

    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(event.dataTransfer.files || []);
    addFilesToAttachments(files);
  }, [addFilesToAttachments, allowAttachments, dragEventHasFiles]);

  const getFileIcon = useCallback((type: string) => {
    if (type.startsWith('image/')) return Image;
    if (type.includes('pdf') || type.includes('document') || type.includes('text')) return FileText;
    return File;
  }, []);

  const formatFileSize = useCallback((bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }, []);

  // ---- VOICE RECORDING HANDLERS ----
  const clearAudioRecordingTimer = useCallback(() => {
    if (audioRecordingTimerRef.current !== null) {
      window.clearInterval(audioRecordingTimerRef.current);
      audioRecordingTimerRef.current = null;
    }
  }, []);

  const stopAudioStream = useCallback(() => {
    audioStreamRef.current?.getTracks().forEach((track) => track.stop());
    audioStreamRef.current = null;
  }, []);

  const transcribeRecordedAudio = useCallback(async (audioBlob: Blob) => {
    const token = authService.getTokens()?.access_token;
    if (!token) {
      throw new Error('Authentication required');
    }

    const extension = audioBlob.type.includes('ogg') ? 'ogg' : audioBlob.type.includes('mp4') ? 'm4a' : 'webm';
    const file = new globalThis.File([audioBlob], `ai-chat-${Date.now()}.${extension}`, {
      type: audioBlob.type || 'audio/webm',
    });

    const formData = new FormData();
    formData.append('audio', file);
    formData.append('prompt', 'Transcribe this AI assistant voice message accurately. Preserve wording, names, numbers, sentence order, and punctuation where possible.');

    const response = await fetch('/api/v1/ai/transcribe-audio', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    const data = await response.json().catch(() => null);

    if (!response.ok || !data?.success) {
      throw new Error(data?.error || 'Failed to transcribe audio');
    }

    return typeof data?.data?.text === 'string' ? data.data.text.trim() : '';
  }, []);

  const stopSpeechToText = useCallback(async (keepFocus = true, discardAudio = false) => {
    const activeController = speechRecognitionRef.current;
    speechRecognitionRef.current = null;

    const activeRecorder = mediaRecorderRef.current;
    if (activeRecorder) {
      const stream = audioStreamRef.current;

      if (discardAudio) {
        mediaRecorderRef.current = null;
        audioChunksRef.current = [];
        clearAudioRecordingTimer();
        setIsAudioRecording(false);
        setIsTranscribingAudio(false);
        setAudioRecordingDuration(0);

        try {
          activeRecorder.ondataavailable = null;
          activeRecorder.onstop = null;
          activeRecorder.onerror = null;
          if (activeRecorder.state !== 'inactive') {
            activeRecorder.stop();
          }
        } catch {
          // Ignore teardown failures when abandoning an in-flight recording.
        }

        stream?.getTracks().forEach((track) => track.stop());
        audioStreamRef.current = null;
      } else if (activeRecorder.state !== 'inactive') {
        setIsSpeechStarting(false);
        activeRecorder.stop();
      }

      if (keepFocus && discardAudio) {
        focusInput();
      }

      return;
    }

    if (activeController) {
      try {
        await activeController.stop();
      } catch {
        // Ignore browsers/platforms that fail while stopping an inactive recognizer.
      } finally {
        await activeController.destroy();
      }
    }

    setIsListening(false);
    setIsSpeechStarting(false);

    if (keepFocus) {
      focusInput();
    }
  }, [clearAudioRecordingTimer, focusInput]);

  const startRecordedSpeechCapture = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      throw new Error('Audio recording is not supported on this device');
    }

    setIsSpeechStarting(true);

    if (speechRecognitionRef.current) {
      await speechRecognitionRef.current.destroy();
      speechRecognitionRef.current = null;
    }

    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const supportedMimeType = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus',
    ].find((mimeType) => {
      try {
        return typeof MediaRecorder.isTypeSupported === 'function' ? MediaRecorder.isTypeSupported(mimeType) : false;
      } catch {
        return false;
      }
    });

    const mediaRecorder = supportedMimeType
      ? new MediaRecorder(stream, { mimeType: supportedMimeType })
      : new MediaRecorder(stream);

    audioStreamRef.current = stream;
    audioChunksRef.current = [];
    mediaRecorderRef.current = mediaRecorder;
    speechDraftBaseRef.current = inputText;
    setAudioRecordingDuration(0);

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        audioChunksRef.current.push(event.data);
      }
    };

    mediaRecorder.onerror = () => {
      console.warn('[AIAssistantChat] Audio recording error');
      stopAudioStream();
      clearAudioRecordingTimer();
      mediaRecorderRef.current = null;
      setIsAudioRecording(false);
      setIsSpeechStarting(false);
      setAudioRecordingDuration(0);
      notify.error('Audio recording failed before transcription could start');
    };

    mediaRecorder.onstop = async () => {
      const recordedChunks = [...audioChunksRef.current];
      const mimeType = mediaRecorder.mimeType || 'audio/webm';

      audioChunksRef.current = [];
      mediaRecorderRef.current = null;
      clearAudioRecordingTimer();
      stopAudioStream();
      setIsAudioRecording(false);
      setAudioRecordingDuration(0);

      if (!recordedChunks.length) {
        setIsTranscribingAudio(false);
        focusInput();
        return;
      }

      const audioBlob = new Blob(recordedChunks, { type: mimeType });
      if (audioBlob.size === 0) {
        setIsTranscribingAudio(false);
        notify.error('No audio was captured for transcription');
        focusInput();
        return;
      }

      setIsTranscribingAudio(true);

      try {
        const transcript = await transcribeRecordedAudio(audioBlob);

        if (!transcript) {
          notify.error('No speech was detected in the recording');
          return;
        }

        setInputText(appendRecordedSpeechToChatInput(speechDraftBaseRef.current, transcript));
        notify.success('Voice message transcribed');
      } catch (error: unknown) {
        console.error('[AIAssistantChat] Audio transcription failed:', error);
        notify.error(getErrorMessage(error, 'Audio transcription failed'));
      } finally {
        setIsTranscribingAudio(false);
        focusInput();
      }
    };

    mediaRecorder.start();
    setIsAudioRecording(true);
    setIsSpeechStarting(false);

    audioRecordingTimerRef.current = window.setInterval(() => {
      setAudioRecordingDuration((prev) => prev + 1);
    }, 1000);
  }, [clearAudioRecordingTimer, focusInput, inputText, stopAudioStream, transcribeRecordedAudio]);

  const startSpeechToText = useCallback(async () => {
    if (isGenerating || (creditsTotal > 0 && creditsRemaining <= 0)) {
      return;
    }

    if (isSpeechStarting) {
      return;
    }

    if (isAudioRecording) {
      await stopSpeechToText();
      return;
    }

    if (isTranscribingAudio) {
      return;
    }

    if (isListening) {
      await stopSpeechToText();
      return;
    }

    // Always prefer recorded transcription (MediaRecorder → Google Cloud
    // Speech-to-Text via /api/v1/ai/transcribe-audio) when available — it's
    // far more accurate than the live webkitSpeechRecognition which only
    // captures fragments and overwrites them.
    const canUseRecordedTranscription = typeof navigator !== 'undefined' && typeof MediaRecorder !== 'undefined' && Boolean(navigator.mediaDevices?.getUserMedia);

    if (canUseRecordedTranscription) {
      try {
        await startRecordedSpeechCapture();
        return;
      } catch (error) {
        console.warn('[AIAssistantChat] Recorded transcription unavailable, falling back to live speech recognition', error);
        setIsSpeechStarting(false);
      }
    }

    setIsSpeechStarting(true);

    const supported = speechRecognitionSupported || await isSpeechToTextSupported();
    setSpeechRecognitionSupported(supported);

    if (!supported) {
      setIsSpeechStarting(false);
      const isOnCapacitor = isCapacitor();
      console.warn('[AIAssistantChat] Speech-to-text not supported', { isOnCapacitor, supported });
      notify.error(
        isOnCapacitor
          ? 'Speech-to-text plugin not available on this device'
          : 'Speech-to-text is not supported in this browser'
      );
      return;
    }

    if (speechRecognitionRef.current) {
      await speechRecognitionRef.current.destroy();
      speechRecognitionRef.current = null;
    }

    const buildController = (usePopup: boolean) => createSpeechToTextController({
      continuous: true,
      interimResults: true,
      androidPopup: usePopup,
      androidPrompt: usePopup ? 'Speak your message' : undefined,
      onListeningChange: (nextListening) => {
        setIsListening(nextListening);
        if (nextListening) {
          setIsSpeechStarting(false);
        }
      },
      onResult: (snapshot) => {
        setInputText(mergeSpeechIntoChatInput(speechDraftBaseRef.current, snapshot.transcript));
      },
      onError: (error) => {
        if (error.code === 'aborted' || error.code === 'no-speech' || error.code === 'no-match') {
          return;
        }

        console.warn('[AIAssistantChat] Speech recognition error', {
          code: error.code,
          fatal: error.fatal,
          source: error.source,
          mode: usePopup ? 'popup' : 'background',
        });

        if (error.fatal || error.code === 'client' || error.code === 'busy' || error.code === 'start-failed') {
          notify.error(error.message);
        }
      },
    });

    const tryStartController = async (usePopup: boolean): Promise<boolean> => {
      const controller = buildController(usePopup);
      speechRecognitionRef.current = controller;

      const started = await controller.start();
      if (started) {
        return true;
      }

      if (speechRecognitionRef.current === controller) {
        speechRecognitionRef.current = null;
      }

      await controller.destroy();
      return false;
    };

    speechDraftBaseRef.current = inputText;

    const prefersBackgroundMode = isCapacitor();
    const started = await tryStartController(false);

    if (!started && prefersBackgroundMode) {
      const popupStarted = await tryStartController(true);
      if (popupStarted) {
        setIsSpeechStarting(false);
        return;
      }
    }

    setIsSpeechStarting(false);

    if (!started) {
      setIsListening(false);
      notify.error('Unable to start speech-to-text right now');
    }
  }, [creditsRemaining, creditsTotal, inputText, isAudioRecording, isGenerating, isListening, isSpeechStarting, isTranscribingAudio, speechRecognitionSupported, startRecordedSpeechCapture, stopSpeechToText]);

  // Cleanup recording on unmount
  useEffect(() => {
    return () => {
      void stopSpeechToText(false, true);
    };
  }, [stopSpeechToText]);

  // Minimized state - show floating button
  if (isMinimized && onMinimize) {
    return (
      <button
        onClick={onMinimize}
        className="fixed bottom-6 right-6 p-4 text-white rounded-full shadow-lg hover:shadow-xl transition-all z-50"
        style={{ background: `linear-gradient(to right, ${gradientFrom}, ${gradientTo})` }}
      >
        {headerIcon || <Sparkles className="w-6 h-6 text-white" />}
      </button>
    );
  }

  // ---- Floating panel mode ----
  // Renders as a fixed bottom-right chat panel (like the GuidedTour chat)
  // with a blue border. Supports an optional launcher button, minimize, and
  // dock-to-left/right toggles.
  if (floatingPanel) {
    // Launcher button (when closed and launcher enabled)
    if (floatingShowLauncher && !floatingVisible) {
      if (!portalTarget) return null;
      return createPortal(
        <button
          type="button"
          onClick={() => {
            setFloatingVisible(true);
            setFloatingMinimized(false);
          }}
          className="pointer-events-auto fixed bottom-4 right-4 z-[200] flex h-12 w-12 items-center justify-center rounded-full border border-blue-200/80 bg-white/95 shadow-xl backdrop-blur-md transition-all hover:scale-105 dark:border-blue-800/80 dark:bg-slate-800/95"
          title={title}
          aria-label={`Open ${title}`}
        >
          <Brain className="h-5 w-5 text-blue-500 dark:text-blue-400" />
        </button>,
        portalTarget,
      );
    }

    // Minimized bubble
    if (floatingMinimized) {
      if (!portalTarget) return null;
      return createPortal(
        <button
          type="button"
          onClick={() => setFloatingMinimized(false)}
          className="pointer-events-auto fixed bottom-4 z-[200] flex items-center gap-2 rounded-full border border-blue-200/80 bg-white/95 py-1.5 pl-1.5 pr-3 shadow-xl backdrop-blur-md transition-all hover:scale-105 dark:border-blue-800/80 dark:bg-slate-800/95"
          style={floatingDockSide === 'right' ? { right: '1rem' } : { left: '1rem' }}
          title={`Restore ${title}`}
          aria-label={`Restore ${title}`}
        >
          <div
            className="flex h-9 w-9 items-center justify-center rounded-full text-white"
            style={{ background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})` }}
          >
            <Sparkles className="h-4 w-4" />
          </div>
          <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">{title}</span>
        </button>,
        portalTarget,
      );
    }
  }

  const panelContent = (
    <div
      ref={containerRef}
      onClick={handleContainerClick}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        floatingPanel
          ? `pointer-events-auto fixed bottom-4 z-[200] flex h-[min(70vh,540px)] w-[min(92vw,380px)] animate-[tour-card-slide_0.4s_ease-out_both] flex-col overflow-hidden rounded-2xl border-2 ${floatingBorderClass} bg-white/95 shadow-2xl backdrop-blur-md transition-[left,right] duration-300 ease-out dark:bg-slate-800/95`
          : 'relative flex h-full flex-col overflow-hidden bg-gray-50 dark:bg-gray-900',
        className
      )}
      style={floatingPanel ? { ...(floatingDockSide === 'right' ? { right: '1rem' } : { left: '1rem' }), ...(floatingBorderColor ? { borderColor: floatingBorderColor } : {}) } : undefined}
      onWheel={floatingPanel ? (e) => e.stopPropagation() : undefined}
      onTouchMove={floatingPanel ? (e) => e.stopPropagation() : undefined}
    >
      {/* Scroll to top anchor */}
      <div ref={messagesTopRef} className="scroll-to-top-trigger" />

      {/* Header */}
      {floatingPanel ? (
        <div className="flex items-center justify-between gap-2 border-b border-slate-200/80 px-3 py-2.5 dark:border-slate-700/80">
          <div className="flex min-w-0 items-center gap-2">
            <div
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-white"
              style={{ background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})` }}
            >
              <Sparkles className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span
                  className="rounded-full px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider text-white"
                  style={{ backgroundColor: primaryColor || gradientFrom }}
                >
                  {floatingBadgeLabel ?? 'AI Assistant'}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <p className="truncate text-xs font-semibold text-slate-700 dark:text-slate-200">{title}</p>
                {titleExtra}
              </div>
              {subtitle && (
                <p className="truncate text-[10px] text-slate-400 dark:text-slate-500">{subtitle}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-0.5">
            {coachToggle && (
              <button
                type="button"
                onClick={handleCoachToggle}
                className="mr-0.5 inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold text-white transition-opacity hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
                title="Switch to AI Training Coach"
                aria-label="Switch to AI Training Coach"
              >
                <GraduationCap className="h-3 w-3" />
                Coach
              </button>
            )}
            {extraHeaderActions?.map((action) => {
              const ActionIcon = action.icon;
              const showLabel = action.showLabel === 'always' && !!action.label;
              return (
                <button
                  key={action.id ?? action.label ?? action.title}
                  type="button"
                  onClick={action.onClick}
                  disabled={action.disabled}
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] font-semibold transition-colors',
                    action.variant === 'solid' || action.variant === 'primary'
                      ? 'text-white hover:opacity-90'
                      : 'border border-slate-300 text-slate-600 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700',
                    action.disabled && 'opacity-50'
                  )}
                  style={
                    action.variant === 'solid' || action.variant === 'primary'
                      ? { backgroundColor: primaryColor }
                      : undefined
                  }
                  title={action.title ?? action.label}
                  aria-label={action.title ?? action.label}
                  data-tour-id={action.dataTourId}
                >
                  <ActionIcon className="h-3 w-3" />
                  {showLabel && action.label}
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => setFloatingDockSide((prev) => (prev === 'right' ? 'left' : 'right'))}
              className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
              title={floatingDockSide === 'right' ? 'Move to left' : 'Move to right'}
              aria-label={floatingDockSide === 'right' ? 'Move panel to left' : 'Move panel to right'}
            >
              {floatingDockSide === 'right' ? <ArrowLeft className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
            </button>
            <button
              type="button"
              onClick={() => (onFloatingMinimize ? onFloatingMinimize() : setFloatingMinimized(true))}
              className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
              title="Minimize"
              aria-label="Minimize assistant"
            >
              <Minimize2 className="h-4 w-4" />
            </button>
            {closable && onClose && (
              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
                title="Close"
                aria-label="Close assistant"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      ) : !hideHeader ? (
        <ModuleHeader
          variant="compact"
          icon={resolvedHeaderIcon}
          title={title}
          titleExtra={titleExtra}
          subtitle={subtitle}
          moduleKey={resolvedModuleKey}
          primaryColor={primaryColor}
          centerContent={headerCenterContent}
          actions={headerActions}
          storageKey={resolvedHeaderStorageKey}
          defaultMinimized={false}
          className={headerClassName}
        />
      ) : null}

      {/* Messages Area */}
      <div className="relative flex flex-1 min-h-0 overflow-hidden px-2 pb-2 sm:px-3 sm:pb-3">
        {/* History panel — sidebar in embedded mode; full overlay in floating
            mode (the panel is too narrow for a side-by-side list). */}
        {historyOpen && showHistory && (
          <div className={cn(
            'flex flex-col overflow-hidden border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800/95',
            floatingPanel
              ? 'absolute inset-2 z-20 rounded-2xl'
              : 'z-10 mr-2 h-full w-72 flex-shrink-0 rounded-2xl',
          )}>
            {/* History Header */}
            <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-3 dark:border-gray-700 dark:bg-gray-900/60">
              <div className="flex items-center gap-2">
                <History className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Chat History</h3>
              </div>
              <button
                onClick={() => setHistoryOpen(false)}
                className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded transition-colors"
              >
                <X className="w-3.5 h-3.5 text-gray-500" />
              </button>
            </div>

            {/* New Chat Button */}
            {onNewChat && (
              <div className="border-b border-gray-100 px-3 py-2 dark:border-gray-700/80">
                <button
                  onClick={() => {
                    handleNewChatClick();
                    setHistoryOpen(false);
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                  style={{ 
                    backgroundColor: `${primaryColor}15`, 
                    color: primaryColor 
                  }}
                >
                  <RefreshCw className="w-3.5 h-3.5" />
                  New Chat
                </button>
              </div>
            )}

            {/* Sessions List */}
            <div className="flex-1 overflow-y-auto">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : sessionHistory.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                  <MessageCircle className="w-8 h-8 text-gray-300 dark:text-gray-600 mb-2" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">No previous chats</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Start a conversation to see it here</p>
                </div>
              ) : (
                <div className="py-2">
                  {sessionHistory.map((session, index) => {
                    // Group by date label
                    const dateLabel = formatSessionDate(session.date);
                    const prevSession = index > 0 ? sessionHistory[index - 1] : null;
                    const prevDateLabel = prevSession ? formatSessionDate(prevSession.date) : null;
                    const showDateHeader = dateLabel !== prevDateLabel;

                    return (
                      <React.Fragment key={session.id}>
                        {showDateHeader && (
                          <div className="px-4 py-1.5 mt-1 first:mt-0">
                            <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500">
                              {dateLabel}
                            </span>
                          </div>
                        )}
                        <button
                          onClick={() => handleLoadSession(session.id, session.date)}
                          disabled={loadingSessionId === session.id}
                          className={cn(
                            "w-full text-left px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group",
                            loadingSessionId === session.id && "opacity-60"
                          )}
                        >
                          <div className="flex items-start gap-2.5">
                            <div className="p-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg flex-shrink-0 mt-0.5">
                              {loadingSessionId === session.id ? (
                                <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin" />
                              ) : (
                                <MessageCircle className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                {moduleContext ? `${moduleContext.charAt(0).toUpperCase() + moduleContext.slice(1)} Chat` : 'Chat Session'}
                              </p>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {session.messageCount} message{session.messageCount !== 1 ? 's' : ''}
                                </span>
                                <span className="text-gray-300 dark:text-gray-600">·</span>
                                <span className="text-xs text-gray-400 dark:text-gray-500">
                                  {new Date(session.lastActivity).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            </div>
                          </div>
                        </button>
                      </React.Fragment>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Chat Content */}
        <div ref={messagesContainerRef} className="flex-1 min-h-0 overflow-y-auto rounded-2xl border border-gray-200 bg-white px-3 py-3 shadow-sm dark:border-gray-700 dark:bg-gray-800/95 md:px-5 md:py-4">
        {/* Welcome Screen */}
        {messages.length === 0 && (
          <div className="flex h-full flex-col items-center justify-start px-2 pt-6 pb-4 text-center sm:pt-10">
            {/* Welcome icon */}
            <div 
              className="mb-3 flex h-14 w-14 items-center justify-center rounded-full text-white shadow-lg sm:h-16 sm:w-16"
              style={{ background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})` }}
            >
              <Sparkles className="h-7 w-7 sm:h-8 sm:w-8" />
            </div>
            
            {/* Welcome Title with User Name */}
            <h2 className="mb-1 text-xl font-bold text-gray-900 dark:text-white md:text-2xl">
              {userName ? `Welcome, ${userName}` : (welcomeTitle || `Welcome to ${title}`)}
            </h2>
            
            {/* Subtitle/Message */}
            <p className="max-w-xl text-sm text-gray-500 dark:text-gray-400">
              {welcomeMessage || 'Get started by asking a question.'}
            </p>

            {showCredits && (
              <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-semibold text-gray-600 dark:border-gray-700 dark:bg-gray-900/70 dark:text-gray-300">
                <Sparkles className="h-3.5 w-3.5" style={{ color: primaryColor }} />
                {creditsRemaining} credit{creditsRemaining === 1 ? '' : 's'} available
              </div>
            )}
          </div>
        )}

        {/* Chat Messages */}
        {messages.length > 0 && (
          <div className="space-y-4">
            {messages.map((message) => (
              <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] ${message.role === 'user' ? 'order-2' : ''}`}>
                  {/* Message Bubble */}
                  <div className={cn(
                    'rounded-2xl px-4 py-3 shadow-sm',
                    message.role === 'user' 
                      ? 'text-white rounded-br-sm' 
                      : 'bg-gray-50 dark:bg-gray-900/70 text-gray-900 dark:text-white rounded-bl-sm border border-gray-200 dark:border-gray-700'
                  )}
                  style={message.role === 'user' ? { 
                    background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})` 
                  } : undefined}
                  >
                    <div className="text-sm leading-relaxed">
                      {message.role === 'assistant' ? renderMessageContent(message.content) : <p className="whitespace-pre-wrap">{message.content}</p>}
                    </div>
                  </div>

                  {/* Attachments */}
                  {message.attachments && message.attachments.length > 0 && renderAttachment && (
                    <div className="mt-3 space-y-2">
                      {message.attachments.map((attachment, i) => (
                        <div key={i}>
                          {renderAttachment(attachment)}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Suggested Actions */}
                  {message.actions && message.actions.length > 0 && (onApplyAction || message.actions.some((a) => a.type === 'start_tour' || a.type === 'open_coach')) && (
                    <div className="mt-3">
                      {renderActions ? (
                        renderActions(message.actions, handleApplyActionInternal)
                      ) : (
                        <DefaultActionsCard actions={message.actions} onApply={handleApplyActionInternal} primaryColor={primaryColor} />
                      )}
                    </div>
                  )}

                  {/* Feedback & Timestamp */}
                  {message.role === 'assistant' && (
                    <div className="flex items-center gap-2 mt-2 px-1">
                      <span className="text-xs text-gray-400 font-medium">
                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <div className="flex items-center gap-0.5">
                        <button
                          onClick={() => handleCopyMessage(message.id, message.content)}
                          className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 transition-colors"
                          title="Copy message"
                        >
                          {copiedMessageId === message.id ? (
                            <Check className="w-3.5 h-3.5 text-green-500" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                        </button>
                        {onFeedback && (
                          <>
                            <button
                              onClick={() => onFeedback(message.id, 'positive')}
                              className={cn(
                                'p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors',
                                message.feedback === 'positive' ? 'text-green-500' : 'text-gray-400'
                              )}
                            >
                              <ThumbsUp className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => onFeedback(message.id, 'negative')}
                              className={cn(
                                'p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors',
                                message.feedback === 'negative' ? 'text-red-500' : 'text-gray-400'
                              )}
                            >
                              <ThumbsDown className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Generating indicator */}
            {isGenerating && (
              <div className="flex justify-start">
                <div className="rounded-2xl rounded-bl-sm border border-gray-200 bg-gray-50 px-4 py-3 shadow-sm dark:border-gray-700 dark:bg-gray-900/70">
                  <div className="flex items-center gap-3">
                    <Loader2 className="w-4 h-4 animate-spin" style={{ color: primaryColor }} />
                    <span className="text-sm text-gray-500 dark:text-gray-400 font-medium">Thinking...</span>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>
      </div>

      {/* Credit Warning Banners — Phase 11 */}
      {showCredits && creditsTotal > 0 && creditsRemaining <= 0 && (
        <div className="mx-2 flex-shrink-0 rounded-2xl border border-red-200 bg-red-50 px-3 py-2 dark:border-red-800 dark:bg-red-900/20 sm:mx-3">
          <div className="flex items-center gap-2 text-sm text-red-700 dark:text-red-300">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>
              <strong>Monthly AI limit reached.</strong>
              {creditsResetDate && ` Resets ${new Date(creditsResetDate).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long' })}.`}
            </span>
          </div>
        </div>
      )}
      {showCredits && creditsTotal > 0 && creditsRemaining > 0 && (creditsRemaining / creditsTotal) <= 0.1 && (
        <div className="mx-2 flex-shrink-0 rounded-2xl border border-orange-200 bg-orange-50 px-3 py-1.5 dark:border-orange-800 dark:bg-orange-900/20 sm:mx-3">
          <div className="flex items-center gap-2 text-xs text-orange-700 dark:text-orange-300">
            <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Almost out of AI credits ({creditsRemaining} left){creditsResetDate && `. Resets ${new Date(creditsResetDate).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long' })}`}.</span>
          </div>
        </div>
      )}

      {/* Input Area - Compact design */}
      <div className="flex-shrink-0 px-2 pb-2 pt-2 sm:px-3 sm:pb-3">
        {/* Hidden file input */}
        {allowAttachments && (
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.json"
            className="hidden"
            onChange={handleFileChange}
          />
        )}
        <div className="mx-auto max-w-4xl">
          {allowAttachments && isDragOver && (
            <div className="mb-2 rounded-2xl border-2 border-dashed bg-white/95 px-4 py-3 shadow-lg dark:bg-gray-800/95">
              <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: primaryColor }}>
                <Paperclip className="w-4 h-4" />
                Drop files here to attach them
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                They’ll appear below immediately, just like files added with Browse.
              </p>
            </div>
          )}

          {/* Attached files preview */}
          {allowAttachments && attachedFiles.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-1.5">
              {attachedFiles.map((file, i) => {
                const IconComp = getFileIcon(file.type);
                return (
                  <div key={i} className="flex items-center gap-1.5 px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 text-xs">
                    <IconComp className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300 max-w-[120px] truncate">{file.name}</span>
                    <span className="text-gray-400">{formatFileSize(file.size)}</span>
                    <button onClick={() => handleRemoveFile(i)} className="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded">
                      <X className="w-3 h-3 text-gray-400" />
                    </button>
                  </div>
                );
              })}
            </div>
          )}

          <div 
            className="flex flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all duration-200 focus-within:ring-2 dark:border-gray-700 dark:bg-gray-800"
            style={{ 
              '--tw-ring-color': primaryColor,
            } as React.CSSProperties}
          >
            {/* Voice recording indicator */}
            {(isListening || isSpeechStarting || isAudioRecording || isTranscribingAudio) && (
              <div className="flex items-center gap-3 px-3 py-2 border-b border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20">
                {(isAudioRecording || isListening) ? (
                  <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                ) : (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-red-500 dark:text-red-400" />
                )}
                <span className="text-xs font-medium text-red-600 dark:text-red-400">
                  {isTranscribingAudio
                    ? 'Transcribing your voice message…'
                    : isAudioRecording
                      ? `Recording… ${audioRecordingDuration}s — tap stop when you’re done`
                      : isSpeechStarting
                        ? 'Starting speech-to-text…'
                        : 'Listening… speak now and your words will be added to the message'}
                </span>
                {(isAudioRecording || isListening) && (
                  <button
                    onClick={() => void stopSpeechToText()}
                    className="ml-auto flex items-center gap-1 rounded-lg bg-red-600 px-2 py-1 text-xs font-medium text-white transition-colors hover:bg-red-700"
                  >
                    <StopCircle className="w-3.5 h-3.5" />
                    Stop
                  </button>
                )}
              </div>
            )}

            {/* Textarea for multi-line input */}
            <textarea
              ref={inputRef as React.RefObject<HTMLTextAreaElement>}
              value={inputText}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSendMessage();
                }
              }}
              placeholder={inputPlaceholder}
              rows={inputRows}
              className={cn(
                'flex-1 px-3 py-2.5 bg-transparent border-0 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:ring-0 focus:outline-none resize-none overflow-y-auto',
                inputMinHeightClassName,
                inputMaxHeightClassName
              )}
              disabled={isGenerating || isAudioRecording || isTranscribingAudio || (creditsTotal > 0 && creditsRemaining <= 0)}
            />
            
            {/* Action buttons row - Compact */}
            <div className="flex items-center justify-between px-2 py-1.5 border-t border-gray-100 dark:border-gray-700/50">
              <div className="flex items-center gap-0.5">
                {allowAttachments && (
                  <button
                    onClick={handleAttachClick}
                    className={cn(
                      "flex items-center gap-1 px-2 py-1 rounded transition-colors",
                      attachedFiles.length > 0 || isDragOver
                        ? "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20"
                        : "text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700/50"
                    )}
                    title="Attach file"
                    type="button"
                  >
                    <Paperclip className="w-3.5 h-3.5" />
                    <span className="text-[11px] font-medium hidden sm:inline">
                      {attachedFiles.length > 0 ? `${attachedFiles.length} file${attachedFiles.length > 1 ? 's' : ''}` : isDragOver ? 'Drop to attach' : 'Attach'}
                    </span>
                  </button>
                )}
                <button
                  onClick={() => void (isListening || isAudioRecording ? stopSpeechToText() : startSpeechToText())}
                  disabled={isGenerating || isSpeechStarting || isTranscribingAudio || (creditsTotal > 0 && creditsRemaining <= 0)}
                  className={cn(
                    "flex items-center gap-1 px-2 py-1 rounded transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                    (isListening || isAudioRecording)
                      ? "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20"
                      : isSpeechStarting || isTranscribingAudio
                        ? "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700/50"
                  )}
                  title={isAudioRecording
                    ? 'Stop recording and transcribe'
                    : isTranscribingAudio
                      ? 'Transcribing voice message'
                      : isSpeechStarting
                        ? 'Starting speech-to-text'
                        : isCapacitor()
                          ? 'Record voice message'
                          : speechRecognitionSupported
                            ? 'Use speech-to-text'
                            : 'Try speech-to-text'}
                  type="button"
                >
                  {isSpeechStarting || isTranscribingAudio ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (isListening || isAudioRecording) ? (
                    <MicOff className="w-3.5 h-3.5" />
                  ) : (
                    <Mic className="w-3.5 h-3.5" />
                  )}
                  <span className="text-[11px] font-medium hidden sm:inline">
                    {isTranscribingAudio
                      ? 'Transcribing…'
                      : isAudioRecording
                        ? `Stop (${audioRecordingDuration}s)`
                        : isSpeechStarting
                          ? 'Starting…'
                          : isListening
                            ? 'Stop Listening'
                            : 'Voice Message'}
                  </span>
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                {showInputCharCounter && (
                  <span className="text-xs text-gray-400 font-medium">
                    {inputCounterLabel} {inputText.length.toLocaleString()}/{normalizedMaxInputChars.toLocaleString()} chars
                  </span>
                )}
                {/* Send button */}
                <button
                  onClick={() => handleSendMessage()}
                  disabled={!inputText.trim() || isGenerating || isSpeechStarting || isListening || isAudioRecording || isTranscribingAudio || (creditsTotal > 0 && creditsRemaining <= 0)}
                  className="p-2 text-white rounded-lg hover:opacity-90 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md"
                  style={{ background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})` }}
                >
                  {isGenerating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          </div>
          
          {/* Disclaimer */}
          {showDisclaimer && (
            <p className="py-1 text-center text-[10px] leading-none text-gray-400">
              {disclaimerText}
            </p>
          )}
        </div>
      </div>
    </div>
  );

  // Render the floating panel through a portal to document.body so its fixed
  // positioning escapes any parent stacking/containing block (e.g. modals with
  // overflow-hidden or transform-based containment). This mirrors the approach
  // used by GuidedTour chat and prevents the dock-side toggle from creating
  // visual duplicates inside modal contexts.
  if (floatingPanel) {
    if (!portalTarget) return null;
    return createPortal(panelContent, portalTarget);
  }

  return panelContent;
}

// ============================================================================
// DEFAULT ACTIONS CARD COMPONENT
// ============================================================================

interface DefaultActionsCardProps {
  actions: AISuggestedAction[];
  onApply: (action: AISuggestedAction) => void;
  primaryColor: string;
}

function DefaultActionsCard({ actions, onApply, primaryColor }: DefaultActionsCardProps) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-amber-100 dark:bg-amber-900/40 rounded-lg">
            <Sparkles className="w-4 h-4 text-amber-600 dark:text-amber-400" />
          </div>
          <span className="font-semibold text-sm text-gray-900 dark:text-white">Recommended Actions</span>
        </div>
      </div>
      <div className="divide-y divide-gray-100 dark:divide-gray-700">
        {actions.map((action) => {
          const isTourAction = action.type === 'start_tour';
          const isCoachAction = action.type === 'open_coach';
          const ActionIcon = isTourAction ? HelpCircle : isCoachAction ? GraduationCap : Sparkles;
          return (
            <div key={action.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors">
              <div className="flex items-center gap-3">
                <div className={cn(
                  'p-2 rounded-xl',
                  isCoachAction
                    ? 'bg-red-100 dark:bg-red-900/30'
                    : 'bg-gray-100 dark:bg-gray-700'
                )}>
                  <ActionIcon className={cn(
                    'w-4 h-4',
                    isCoachAction ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'
                  )} />
                </div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{action.label}</p>
              </div>
              <button
                onClick={() => onApply(action)}
                disabled={action.applied}
                className={cn(
                  'px-4 py-2 text-xs font-semibold rounded-xl transition-all',
                  action.applied
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 cursor-default'
                    : isCoachAction
                      ? 'bg-red-500 text-white hover:bg-red-600 hover:shadow-md'
                      : 'text-white hover:opacity-90 hover:shadow-md'
                )}
                style={!action.applied && !isCoachAction ? { background: `linear-gradient(135deg, ${primaryColor}, ${primaryColor}dd)` } : undefined}
              >
                {action.applied ? '✓ Done' : isTourAction ? 'Start Tour' : isCoachAction ? 'Open Coach' : 'Apply'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// EXPORTS
// ============================================================================

export { AIAssistantChat };
